import { IDataSet } from '../Track';
import { RewardType } from '../../RewardHelper';
import { ResourceDataSet } from './Resource';
import { PurchaseFailReason } from '../../SDK/Drivers/PurchaseFailReason';
import { PurchaseStatus } from '../TrackingHelpers/PurchaseStatus';

export enum TransactionType {
    REWARD = 'REWARD', // is when the player receives something for free.
    COIN_PURCHASE = 'COIN_PURCHASE', // is when the player purchases something with coins.
    PURCHASE = 'PURCHASE', // is when the player purchases something with real currency
    AD = 'AD', //is when the resource event had involved an ad
    INVENTORY = 'INVENTORY', // is when the player uses an item from their inventory (like a booster in a level)"
    LEVEL_END = 'LEVEL_END', // when the player ends a level
    LUCKY_TURNIPS_CLAIM = 'TURNIP_PICK', // when the player claims rewards after picking turnips in the star chest minigame
    PET = 'PET', // when the player claims rewards from the pet minigame
}

/**
 * This describes the action that lead to the transaction or resource event
 */
export enum TActionType {
    WHEEL_SPIN = 'WHEEL_SPIN', // describes a wheel spin
    WHEEL_SPIN_PREMIUM_1 = 'WHEEL_SPIN_PREMIUM_1',
    WHEEL_SPIN_PREMIUM_2 = 'WHEEL_SPIN_PREMIUM_2',
    WHEEL_SPIN_PREMIUM_3 = 'WHEEL_SPIN_PREMIUM_3',
    BOOSTER_USE = 'BOOSTER_USE', // describes the use of boosters. Note: this can be when a level starts (for example, a bomb booster) or within a level
    INVENTORY_PURCHASE = 'INVENTORY_PURCHASE', // describes a purchase made of an item that is added to the inventory and is not immediately used
    HARVEST = 'HARVEST', // describes a resource event resulting from a user harvesting a field
    INVITE = 'INVITE', // describes a resource event resulting from an invitation of another user to the game
    BOT = 'BOT', // describes a transaction as a result of a bot message
    REWARD = 'REWARD', // describes a general reward that the user receives from the game"
    AD = 'AD',
    SOCIAL = 'SOCIAL',
    LEVEL_ENTRY = 'LEVEL_ENTRY',
    LEVEL_END = 'LEVEL_END',
    LUCKY_TURNIPS_CLAIM = 'TURNIP_PICK',
    DAILY_BONUS = 'DAILY_BONUS',
    PET_REWARD_CLAIM = 'PET_REWARD_CLAIM', // describes a resource event when playing the pet minigame
    FEEDING_CHARLY = "FEEDING_CHARLY"
}

export enum TSocialActionType {
    INVITE = 'INVITE',
    GET_COINS = 'GET_COINS',
    SEND_COINS = 'SEND_COINS',
    WALLPOST_SHARE_3_STAR_REWARD = 'WALLPOST_SHARE_3_STAR_REWARD',
    WALLPOST_SHARE_CROP_UNLOCKED = 'WALLPOST_SHARE_CROP_UNLOCKED',
    WALLPOST_SHARE_FIELD_UNLOCKED = 'WALLPOST_SHARE_FIELD_UNLOCKED',
    WALLPOST_SHARE_PET_UNLOCKED = 'WALLPOST_SHARE_PET_UNLOCKED',
    WALLPOST_SHARE_PET_JACKPOT = 'WALLPOST_SHARE_PET_JACKPOT',
    CUSTOM_BEST_HIGH_SCORE_LEVEL = 'CUSTOM_BEST_HIGH_SCORE_LEVEL',
    CUSTOM_UNLOCK_FRIEND_RESPONSE = 'CUSTOM_UNLOCK_FRIEND_RESPONSE',
    CUSTOM_FRIEND_REQUEST = 'CUSTOM_FRIEND_REQUEST',
    CUSTOM_SEND_GIFT = 'CUSTOM_SEND_GIFT',
    CUSTOM_INVITE_FRIEND = 'CUSTOM_INVITE_FRIEND',
    CUSTOM_GIVE_CURRENCY = 'CUSTOM_GIVE_CURRENCY',
    CUSTOM_REQUEST_CURRENCY = 'CUSTOM_REQUEST_CURRENCY',
    CUSTOM_SEND_CURRENCY = 'CUSTOM_SEND_CURRENCY',
}

export enum TGameArea {
    // The resource event was done on the saga map
    MAP = 'MAP',
    // This resource event is the sum of what happened in a level
    LEVEL = 'LEVEL',
    // This resource event is a part of the star chest minigame
    LUCKY_TURNIPS = 'STAR_CHEST',
    // This resource event is a part of the my pet mini game
    MY_PET_MINI_GAME = 'MY_PET_MINI_GAME',
    // This resource event is a part of the my farm scene
    FARM = 'FARM',
    // This resource event was a part of the fail flow for a level "
    FAIL_FLOW = 'FAIL_FLOW',
    // This resources event was for completing a quest
    QUEST_COMPLETE = 'QUEST_COMPLETE',
}

export interface IProductsSpentReceived {
    items?: Object[];
    realCurrency?: IRealCurrency;
    virtualCurrencies?: Object[];
}

export interface IRealCurrency {
    realCurrencyType: string;
    realCurrencyAmount: number;
}

export class TransactionDataSet implements IDataSet {
    /**
     * Type of transaction
     */
    transactionType: TransactionType = undefined;
    /**
     * Area the event occurs in
     */
    tGameArea: TGameArea = undefined;

    /**
     * This describes the action that lead to the transaction or resource event
     */
    tActionType: TActionType = undefined;

    /**
     * This describes the id of the product involved with the transaction
     */
    tProductID: string = undefined;

    /**
     * This denotes the level that the transaction might be attatched to.
     * Note: if the transaction was done on the map screen,
     * but on a screen relating to a level, this should note that level!
     */
    tLevelContext: number = 0;
    /**
     * "This denotes how many cards are left in a level when transaction is made
     * (note that in the case of buying extra moves in order to contine playing a the end of a level, this will be ""0"")
     * When the transaction is made NOT from a level and therefore ""moves left"" is irrelevant - this will be -1"
     */
    tCardsLeft: number = -1;
    /**
     * This is a unique ID for the transaction. This is especially important for real money transactions
     */
    transactionID: string = undefined;
    /*
    realCurrencyAmount: number = 0;
    realCurrencyType: string = undefined; */
    /**
     * This is a data object containing the real currency data
     */
    productsSpent: IProductsSpentReceived = undefined;

    /**
     * This is a data object containing the items and currencies received
     * NOT USED BY URI ATM but required by the built-in transaction event
     */
    productsReceived: IProductsSpentReceived = undefined;

    /** */
    tCoinsLevelEntry: number = 0;
    /**
     * Denotes the number of coins received by the player as a reward at the end of a level
     */
    tCoinsLevelEnd: number = 0;
    /**
     * Denoted the number of coins the player got via watching rewarded ads
     */
    tAdCoins: number = 0;
    /**
     * Denotes the number of coins the player got via in app purchases
     */
    tIAPCoins: number = 0;
    /**
     * Denotes the status of the purchase made
     */
    tPurchaseStatus: PurchaseStatus = undefined;
    /**
     * Denotes the reason for failing if failed
     */
    tPurchaseFailReason: PurchaseFailReason = undefined;
}

export enum TRewardAction {
    RECEIVED = 'received',
    USED = 'used',
    COINS_SPENT = 'coins_spent',
}

export const rewardTrackingMap: Map<
    RewardType,
    Map<TRewardAction, keyof ResourceDataSet>
> = new Map<RewardType, Map<TRewardAction, keyof ResourceDataSet>>([
    [
        RewardType.JOKER,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tJokerReceived'],
            [TRewardAction.USED, 'tJokerUsed'],
            [TRewardAction.COINS_SPENT, 'tJokerCoins'],
        ]),
    ],
    [
        RewardType.UNDO,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tUndoReceived'],
            [TRewardAction.USED, 'tUndoUsed'],
            [TRewardAction.COINS_SPENT, 'tUndoCoins'],
        ]),
    ],
    [
        RewardType.BOMB,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tMassRemoveReceived'],
            [TRewardAction.USED, 'tMassRemoveUsed'],
            [TRewardAction.COINS_SPENT, 'tMassRemoveCoins'],
        ]),
    ],
    [
        RewardType.SHUFFLE,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tChangerReceived'],
            [TRewardAction.USED, 'tChangerUsed'],
            [TRewardAction.COINS_SPENT, 'tChangerCoins'],
        ]),
    ],
    [
        RewardType.BULLSEYE,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tRemoveOneReceived'],
            [TRewardAction.USED, 'tRemoveOneUsed'],
            [TRewardAction.COINS_SPENT, 'tRemoveOneCoins'],
        ]),
    ],
    [
        RewardType.FREE_PLAY,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tFreeEntryReceived'],
            [TRewardAction.USED, 'tFreeEntryUsed'],
        ]),
    ],
    [
        RewardType.CARDS,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tExtraCardsReceived'],
        ]),
    ],
    [
        RewardType.PRE_BOOSTER_REMOVE_THREE,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tRemoveCardsBoostersReceived'],
            [TRewardAction.USED, 'tRemoveCardsBoostersUsed'],
            [TRewardAction.COINS_SPENT, 'tRemoveCardsBoostersCoins'],
        ]),
    ],
    [
        RewardType.PRE_BOOSTER_JOKER,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tExtraWildcardsBoostersReceived'],
            [TRewardAction.USED, 'tExtraWildcardsBoostersUsed'],
            [TRewardAction.COINS_SPENT, 'tExtraWildcardsBoostersCoins'],
        ]),
    ],
    [
        RewardType.PRE_BOOSTER_CANISTER,
        new Map<TRewardAction, keyof ResourceDataSet>([
            [TRewardAction.RECEIVED, 'tCanisterCardBoostersReceived'],
            [TRewardAction.USED, 'tCanisterCardBoostersUsed'],
            [TRewardAction.COINS_SPENT, 'tCanisterCardBoostersCoins'],
        ]),
    ],
]);
